import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import {
    loadFavoritesFromSupabase,
    loadWatchedFromSupabase,
    loadRecommendationsFromSupabase
} from '../services/supabaseDataService';

// Supabase kontrolü
if (!supabase) {
    console.error('❌ Supabase client oluşturulamadı! Environment variables kontrol edin.');
}

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    // Görev 2: Auth hazır mı durumunu yönet
    // İlk yüklemede true, session kontrol edildikten sonra false
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [authInitialized, setAuthInitialized] = useState(false);

    // isAuthenticated hesaplanmış değer
    const isAuthenticated = !!user && !!session;
    
    // Auth durumu: 'loading' | 'authenticated' | 'unauthenticated'
    const authStatus = loading 
        ? 'loading' 
        : isAuthenticated 
            ? 'authenticated' 
            : 'unauthenticated';

    // Supabase tablolarından verileri yükle ve localStorage'a senkronize et
    const syncFavoritesFromSupabase = async (profileData) => {
        if (!supabase || !profileData || !user) return;

        try {
            const userId = user.id;

            // YENİ SİSTEM: Ayrı tablolardan yükle
            // Kullanıcıya özel localStorage anahtarları
            const favKey = `film_favorites:${userId}`;
            const watchKey = `film_watched:${userId}`;
            const recKey = `film_recommendations:${userId}`;

            // FAVORİLER - user_favorites tablosundan
            const favoritesFromTable = await loadFavoritesFromSupabase(userId);
            if (favoritesFromTable.length > 0) {
                localStorage.setItem(favKey, JSON.stringify(favoritesFromTable));
            } else {
                // Tabloda veri yoksa localStorage'ı temizle
                localStorage.removeItem(favKey);
                // Eski anahtarı da temizle
                localStorage.removeItem('film_favorites');
            }

            // İZLENENLER - user_watched tablosundan
            const watchedFromTable = await loadWatchedFromSupabase(userId);
            if (watchedFromTable.length > 0) {
                localStorage.setItem(watchKey, JSON.stringify(watchedFromTable));
            } else {
                // Tabloda veri yoksa localStorage'ı temizle
                localStorage.removeItem(watchKey);
                // Eski anahtarı da temizle
                localStorage.removeItem('film_watched');
            }

            // ÖNERİLER - user_recommendations tablosundan
            const recommendationsFromTable = await loadRecommendationsFromSupabase(userId);
            if (recommendationsFromTable.length > 0) {
                localStorage.setItem(recKey, JSON.stringify(recommendationsFromTable));
            } else {
                // Tabloda veri yoksa localStorage'ı temizle
                localStorage.removeItem(recKey);
                // Eski anahtarı da temizle
                localStorage.removeItem('film_recommendations');
            }

        } catch (error) {
            console.error('Veri senkronizasyon hatası:', error);
            // Hata durumunda localStorage'dan yüklemeyi dene (fallback)
        }
    };

    // Yeni kullanıcı için localStorage'ı temizle
    const clearUserLocalStorage = () => {
        try {
            // Tüm kullanıcı verilerini temizle
            localStorage.removeItem('film_recommendations');

            // Favoriler ve izlenenler için doğru key'leri temizle
            // favoriteService.js'deki FAVORITES_KEY = 'film_favorites'
            // favoriteService.js'deki WATCHED_KEY = 'film_watched'
            localStorage.removeItem('film_favorites'); // Ana favoriler key'i
            localStorage.removeItem('film_watched'); // Ana izlenenler key'i

            // Eski/tutarsız key'leri de temizle (güvenlik için)
            localStorage.removeItem('film_app_favorites');
            localStorage.removeItem('film_app_watched');
            localStorage.removeItem('favorites');
            localStorage.removeItem('watched');

            // Eski kullanıcı verisi
            localStorage.removeItem('film_app_user');

            // Favoriler için cleanup date'i de temizle
            localStorage.removeItem('favorite_cleanup_date');

            // Theme tercihini koru
            // localStorage.removeItem('film_app_theme'); // Tema tercihini koruyoruz

            // Global refresh state'leri de temizle (eğer varsa)
            if (window.stopGlobalRefresh) {
                try {
                    window.stopGlobalRefresh();
                } catch (e) {
                    // Ignore if not available
                }
            }

            // "En çok sevilen türler" favorilerden türetiliyor,
            // favoriler temizlenince otomatik olarak sıfırlanacak

            // Yeni kullanıcı için localStorage temizlendi
        } catch (error) {
            console.error('LocalStorage temizleme hatası:', error);
        }
    };

    // Oturum değişikliklerini dinle
    useEffect(() => {
        if (!supabase) {
            setLoading(false);
            setAuthInitialized(true);
            return;
        }

        let mounted = true;

        // URL'de hash varsa (e-posta doğrulama sonrası), önce onu işle
        const handleHash = async () => {
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            if (hashParams.get('access_token') || hashParams.get('type') === 'recovery') {
                // Hash varsa loading göster
                setLoading(true);
                // Session'ı URL'den al
                const { data: { session: hashSession }, error: hashError } = await supabase.auth.getSession();
                if (!mounted) return;

                if (!hashError && hashSession) {
                    setSession(hashSession);
                    setUser(hashSession.user);

                    // window.currentUserId'yi hemen set et (favoriteService için)
                    if (hashSession.user?.id) {
                        window.currentUserId = hashSession.user.id;
                    }

                    // Profil yüklenecek, loadUserProfile içinde yeni kullanıcı kontrolü yapılacak
                    // clearUserLocalStorage() çağrılmayacak - sadece yeni kullanıcıysa temizlenecek

                    await loadUserProfile(hashSession.user.id);
                    // URL'yi temizle
                    window.history.replaceState(null, '', window.location.pathname);
                }
                setLoading(false);
            }
        };

        // Önce hash'i kontrol et, sonra normal session kontrolü
        handleHash().finally(() => {
            // Mevcut oturumu kontrol et
            supabase.auth.getSession()
                .then(({ data: { session }, error }) => {
                    if (!mounted) return;

                    if (error) {
                        console.error('Session hatası:', error);
                        setLoading(false);
                        setAuthInitialized(true);
                        return;
                    }

                    if (session) {
                        setSession(session);
                        setUser(session.user);

                        // window.currentUserId'yi hemen set et (favoriteService için)
                        if (session.user?.id) {
                            window.currentUserId = session.user.id;
                        }

                        if (session.user) {
                            // Profil yükleme arka planda
                            loadUserProfile(session.user.id).finally(() => {
                                if (mounted) {
                                    setLoading(false);
                                    setAuthInitialized(true);
                                }
                            });
                        } else {
                            if (mounted) {
                                setLoading(false);
                                setAuthInitialized(true);
                            }
                        }
                    } else {
                        setSession(null);
                        setUser(null);
                        delete window.currentUserId;
                        setLoading(false);
                        setAuthInitialized(true);
                    }
                })
                .catch((error) => {
                    console.error('Session yükleme hatası:', error);
                    if (mounted) {
                        setLoading(false);
                        setAuthInitialized(true);
                    }
                });
        });

        // Auth state değişikliklerini dinle (arka planda)
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (!mounted) return;

            // Auth state değişiklikleri (sessizce işlenir)

            setSession(session);
            setUser(session?.user ?? null);

            // window.currentUserId'yi hemen set et (favoriteService için)
            if (session?.user?.id) {
                window.currentUserId = session.user.id;
            } else {
                delete window.currentUserId;
            }

            try {
                if (session?.user) {
                    // Profil yükleme arka planda
                    loadUserProfile(session.user.id);
                } else {
                    setProfile(null);
                }
            } catch (error) {
                console.error('Profil yükleme hatası (auth change):', error);
            }
        });

        return () => {
            mounted = false;
            subscription?.unsubscribe();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // user.id değişimini takip et - state resetle ve DB'den yeniden yükle
    useEffect(() => {
        if (!user?.id) {
            // User yoksa localStorage'ı temizle (güvenlik)
            if (window.currentUserId) {
                // Önceki kullanıcının verilerini localStorage'dan kaldır (güvenlik için gerekli değil ama temizlik)
                localStorage.removeItem(`film_favorites:${window.currentUserId}`);
                localStorage.removeItem(`film_watched:${window.currentUserId}`);
                localStorage.removeItem(`film_recommendations:${window.currentUserId}`);
            }
            delete window.currentUserId;
            setProfile(null);
            return;
        }

        // window.currentUserId'yi hemen set et (favoriteService için kritik)
        window.currentUserId = user.id;

        // user.id değişti, profil ve verileri yeniden yükle
        loadUserProfile(user.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    // Kullanıcı profilini yükle
    const loadUserProfile = async (userId) => {
        if (!supabase) return;

        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
                console.error('Profil yükleme hatası:', error);
                return;
            }

            if (data) {
                setProfile(data);
                // Profil verilerini user objesine ekle
                setUser(prev => prev ? { ...prev, profile: data } : null);

                // ÖNCE Supabase'den favoriler ve izlenenleri yükle (supreme source of truth)
                await syncFavoritesFromSupabase(data);

                // Yeni kullanıcı kontrolü - created_at bugünse
                if (data.created_at) {
                    const createdDate = new Date(data.created_at);
                    const today = new Date();
                    const isNewUser = createdDate.toDateString() === today.toDateString();

                    if (isNewUser) {
                        // Yeni kullanıcı için öneriler ve eski localStorage verilerini temizle
                        // Ama favoriler ve izlenenler zaten syncFavoritesFromSupabase ile yönetiliyor
                        localStorage.removeItem('film_recommendations');
                        localStorage.removeItem('favorite_cleanup_date');
                    }
                }
            } else {
                // Profil yoksa oluştur (yeni kullanıcı)
                clearUserLocalStorage(); // Yeni profil = tamamen temiz başlangıç
                await createProfile(userId, null, null);
            }
        } catch (error) {
            console.error('Profil yükleme hatası:', error);
        }
    };

    // Yeni profil oluştur veya güncelle
    const createProfile = async (userId, name, birthDate) => {
        if (!supabase) return null;

        try {
            const { data: { user: authUser } } = await supabase.auth.getUser();

            // Önce mevcut profili kontrol et
            const { data: existingProfile } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            const profileData = {
                id: userId,
                email: authUser?.email || '',
                name: name || authUser?.user_metadata?.name || authUser?.email?.split('@')[0] || 'Kullanıcı',
                // Doğum tarihi: Yeni kayıtta birthDate varsa onu kullan, yoksa mevcut profildeki değeri koru
                birth_date: birthDate !== undefined && birthDate !== null && birthDate !== '' 
                    ? birthDate 
                    : (existingProfile?.birth_date || null),
                preferences: existingProfile?.preferences || {
                    genres: [],
                    favoriteMovies: []
                }
            };

            let data;

            if (existingProfile) {
                // Profil zaten varsa güncelle (özellikle birth_date için)
                // Eğer birthDate parametresi geçilmişse, onu kullan (null/undefined/boş string değilse)
                const updateData = {
                    name: profileData.name,
                    email: profileData.email,
                    // preferences'i sadece boşsa güncelle
                    preferences: existingProfile.preferences || profileData.preferences
                };
                
                // birthDate parametresi geçilmişse ve geçerli bir değerse güncelle
                if (birthDate !== undefined && birthDate !== null && birthDate !== '') {
                    updateData.birth_date = birthDate;
                } else if (profileData.birth_date !== undefined && profileData.birth_date !== null) {
                    // Aksi halde mevcut değeri koru
                    updateData.birth_date = profileData.birth_date;
                }
                
                const { data: updatedProfile, error: updateError } = await supabase
                    .from('profiles')
                    .update(updateData)
                    .eq('id', userId)
                    .select()
                    .single();

                if (updateError) {
                    console.error('Profil güncelleme hatası:', updateError);
                    return null;
                }
                data = updatedProfile;
            } else {
                // Yeni profil oluştur
                const { data: newProfile, error: insertError } = await supabase
                    .from('profiles')
                    .insert([profileData])
                    .select()
                    .single();

                if (insertError) {
                    console.error('Profil oluşturma hatası:', insertError);
                    // Profil zaten varsa yükle
                    if (insertError.code === '23505') { // Unique violation
                        await loadUserProfile(userId);
                        return profile;
                    }
                    return null;
                }
                data = newProfile;
            }

            setProfile(data);
            setUser(prev => prev ? { ...prev, profile: data } : null);
            return data;
        } catch (error) {
            console.error('Profil oluşturma hatası:', error);
            return null;
        }
    };

    // E-posta/şifre ile kayıt
    const register = async (email, password, name, birthDate) => {
        if (!supabase) {
            throw new Error('Supabase bağlantısı yapılandırılmamış. Lütfen .env dosyasını kontrol edin.');
        }

        try {
            // Email'i normalize et (trim ve lowercase)
            const normalizedEmail = email.trim().toLowerCase();

            // Supabase'e kayıt ol - email doğrulama için redirectTo ekle
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: normalizedEmail,
                password,
                options: {
                    data: {
                        name: name || normalizedEmail.split('@')[0]
                    },
                    emailRedirectTo: `${window.location.origin}/verify-email`
                }
            });

            if (authError) {
                // Kullanıcı zaten kayıtlıysa daha açıklayıcı mesaj
                if (authError.message.includes('already registered') || authError.message.includes('User already registered')) {
                    throw new Error('Bu e-posta adresi ile zaten bir hesap mevcut. Lütfen giriş yapın.');
                }
                throw authError;
            }

            if (authData.user) {
                // Kullanıcı bilgilerini set et
                setUser(authData.user);
                setSession(authData.session);

                // Yeni kullanıcı için localStorage'ı temizle (temiz başlangıç)
                if (authData.session) {
                    // Oturum varsa (email doğrulama kapalıysa) hemen temizle
                    clearUserLocalStorage();
                }

                // Profil oluştur veya güncelle (birthDate ile)
                const newProfile = await createProfile(authData.user.id, name, birthDate);
                
                // Profil oluşturulduktan sonra yeniden yükle (tüm verilerin doğru geldiğinden emin ol)
                if (newProfile) {
                    await loadUserProfile(authData.user.id);
                }

                return {
                    user: authData.user,
                    session: authData.session,
                    profile: newProfile
                };
            }

            // E-posta doğrulama gerekirse
            if (!authData.session && authData.user) {
                // Email doğrulama bekleniyor - profil oluştur ama localStorage'ı şimdilik temizleme
                // Email doğrulandıktan sonra temizlenecek
                const newProfile = await createProfile(authData.user.id, name, birthDate);
                
                // Profil oluşturulduktan sonra yeniden yükle (tüm verilerin doğru geldiğinden emin ol)
                if (newProfile) {
                    await loadUserProfile(authData.user.id);
                }

                return {
                    user: authData.user,
                    needsEmailVerification: true,
                    message: 'E-posta adresinize doğrulama linki gönderildi. Lütfen e-postanızı kontrol edin ve giriş yapın.',
                    profile: newProfile
                };
            }

            return authData;
        } catch (error) {
            // Hata durumunda session/user set etme
            setSession(null);
            setUser(null);
            throw new Error(error.message || 'Kayıt işlemi başarısız oldu');
        }
    };

    // E-posta/şifre ile giriş
    const login = async (email, password) => {
        if (!supabase) {
            throw new Error('Supabase bağlantısı yapılandırılmamış. Lütfen .env dosyasını kontrol edin.');
        }

        try {
            // Email'i normalize et (trim ve lowercase)
            const normalizedEmail = email.trim().toLowerCase();

            const { data, error } = await supabase.auth.signInWithPassword({
                email: normalizedEmail,
                password
            });

            if (error) {
                // Daha açıklayıcı hata mesajları
                if (error.message.includes('Invalid login credentials') || error.message.includes('Invalid credentials')) {
                    // Profiles tablosundan email kontrolü yap
                    // Eğer email varsa ama giriş başarısız olursa şifre yanlış
                    // Eğer email yoksa kullanıcı kayıtlı değil
                    try {
                        const { data: profileData } = await supabase
                            .from('profiles')
                            .select('email')
                            .eq('email', normalizedEmail)
                            .maybeSingle(); // maybeSingle kullan - eğer yoksa null döner, hata fırlatmaz

                        // Eğer profiles tablosunda email varsa, kullanıcı var ama şifre yanlış
                        if (profileData && profileData.email === normalizedEmail) {
                            throw new Error('E-posta adresi veya şifre yanlış. Lütfen kontrol edip tekrar deneyin.');
                        }

                        // Eğer profileData yoksa veya error varsa, kullanıcı kayıtlı değil
                        // RLS policy nedeniyle erişim reddedilmiş olabilir, bu durumda da kullanıcı yok kabul edelim
                        throw new Error('Böyle bir kullanıcı bulunmamaktadır. Lütfen kayıt olun.');
                    } catch (checkError) {
                        // Eğer bizim fırlattığımız hata ise (yukarıdaki throw new Error), onu kullan
                        if (checkError.message.includes('E-posta adresi veya şifre yanlış') ||
                            checkError.message.includes('Böyle bir kullanıcı bulunmamaktadır')) {
                            throw checkError;
                        }
                        // Diğer durumlarda (örneğin RLS policy hatası) genel mesaj
                        throw new Error('E-posta adresi veya şifre yanlış. Lütfen kontrol edip tekrar deneyin.');
                    }
                }
                if (error.message.includes('Email not confirmed')) {
                    throw new Error('E-posta adresiniz doğrulanmamış. Lütfen e-postanızdaki doğrulama linkine tıklayın.');
                }
                throw error;
            }

            setSession(data.session);
            setUser(data.user);

            if (data.user) {
                await loadUserProfile(data.user.id);
            }

            return {
                user: data.user,
                session: data.session,
                profile: profile
            };
        } catch (error) {
            // Hata durumunda session/user set etme
            setSession(null);
            setUser(null);
            throw new Error(error.message || 'Giriş işlemi başarısız oldu');
        }
    };

    // Çıkış yap
    const logout = async () => {
        if (!supabase) return;

        try {
            // Çıkış yapmadan önce favoriler ve izlenenleri Supabase'e kaydet
            // Sadece user ve session varsa kaydet
            if (user && session) {
                try {
                    const favorites = localStorage.getItem('film_favorites');
                    const watched = localStorage.getItem('film_watched');

                    if (favorites || watched) {
                        const preferences = {};
                        if (favorites) {
                            preferences.favorites = JSON.parse(favorites);
                        }
                        if (watched) {
                            preferences.watched = JSON.parse(watched);
                        }
                        await updateUserPreferences(preferences);
                    }
                } catch (syncError) {
                    console.error('Çıkış öncesi sync hatası:', syncError);
                    // Sync hatası olsa bile çıkış işlemini devam ettir
                }
            }

            // Session kontrolü - eğer session varsa signOut çağır
            if (session) {
                const { error } = await supabase.auth.signOut();
                if (error && !error.message.includes('session missing')) {
                    // Session missing hatası normal (zaten çıkış yapılmış)
                    throw error;
                }
            }

            // localStorage'ı TAMAMEN temizleme (sadece session bilgileri temizlenmeli)
            // Favoriler ve izlenenler korunmalı (tekrar giriş yapınca yüklenecek)
            // clearUserLocalStorage(); // BU ÇAĞRILMAMALI - çıkışta veriler korunmalı

            setUser(null);
            setSession(null);
            setProfile(null);

            // window.updateUserPreferences'i temizle
            delete window.updateUserPreferences;
        } catch (error) {
            // Session missing hatası normal, diğer hataları fırlat
            if (!error.message || !error.message.includes('session missing')) {
                console.error('Çıkış hatası:', error);
                throw error;
            }
            // Session missing ise sessizce devam et
            setUser(null);
            setSession(null);
            setProfile(null);
            delete window.updateUserPreferences;
        }
    };

    // Kullanıcı tercihlerini güncelle
    const updateUserPreferences = useCallback(async (preferences) => {
        if (!user || !supabase) return;

        try {
            const updatedProfile = {
                ...profile,
                preferences: {
                    ...(profile?.preferences || {}),
                    ...preferences
                },
                updated_at: new Date().toISOString()
            };

            const { data, error } = await supabase
                .from('profiles')
                .update(updatedProfile)
                .eq('id', user.id)
                .select()
                .single();

            if (error) throw error;

            setProfile(data);
            setUser(prev => prev ? { ...prev, profile: data } : null);
        } catch (error) {
            console.error('Tercih güncelleme hatası:', error);
            throw error;
        }
    }, [user, profile]);

    // Profil bilgilerini güncelle
    const updateProfile = async (updates) => {
        if (!user || !supabase) return;

        try {
            const updatedProfile = {
                ...profile,
                ...updates,
                updated_at: new Date().toISOString()
            };

            const { data, error } = await supabase
                .from('profiles')
                .update(updatedProfile)
                .eq('id', user.id)
                .select()
                .single();

            if (error) throw error;

            setProfile(data);
            setUser(prev => prev ? { ...prev, profile: data } : null);

            return data;
        } catch (error) {
            console.error('Profil güncelleme hatası:', error);
            throw error;
        }
    };

    // updateUserPreferences'i window'a ekle (favoriteService'den erişim için)
    // Bu useEffect updateUserPreferences tanımlandıktan SONRA çalışmalı
    useEffect(() => {
        if (updateUserPreferences && user) {
            window.updateUserPreferences = updateUserPreferences;
            window.currentUserId = user.id; // userId'yi window'a ekle
        } else {
            delete window.updateUserPreferences;
            delete window.currentUserId;
        }
        return () => {
            delete window.updateUserPreferences;
            delete window.currentUserId;
        };
    }, [updateUserPreferences, user]);

    const value = {
        user: user ? { ...user, profile } : null,
        profile,
        session,
        isAuthenticated,
        login,
        register,
        logout,
        updateUserPreferences,
        updateProfile,
        loading,
        // Görev 2: Auth durumu bilgilerini ekle
        authStatus, // 'loading' | 'authenticated' | 'unauthenticated'
        authInitialized // İlk yükleme tamamlandı mı?
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
